import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { TasksService } from '../../../services/tasks.service';
import { SyncPhotoIssue, SyncProgressService, SyncProgressState, SyncTaskResult } from '../../../services/sync-progress.service';
import { SessionService } from 'src/app/services/session.service';
import { Router } from '@angular/router';
import { GO_URL, TASKS_URL } from 'src/app/config/url.config';

@Component({
  selector: 'sync-progress-dialog',
  templateUrl: './sync-progress-dialog.component.html',
  styleUrls: ['./sync-progress-dialog.component.scss']
})
export class SyncProgressDialogComponent {
  @ViewChild('logTextarea') logTextarea?: ElementRef<HTMLTextAreaElement>;
  private readonly sub = new Subscription();
  private historyTrapActive = false;
  deletingTaskIds = new Set<number>();
  deletedOfflineTaskIds = new Set<number>();

  state$ = this.syncProgressService.state$;
  viewModel$: Observable<SyncProgressState & {
    globalProgress: number;
    processedTasks: number;
    taskProgressLabel: string;
    itemProgress: number;
    photoProgress: number;
    photoProgressMode: 'determinate' | 'indeterminate';
    showPhotoProgress: boolean;
    photoProgressLabel: string;
    resultBannerClass: 'success' | 'error' | 'neutral';
    failureReasonText: string;
    canClose: boolean;
    canStop: boolean;
    statusText: string;
    pausedStatusText: string;
    sortedTaskResults: SyncTaskResult[];
  }> = this.state$.pipe(
    map(state => {
      const processedTasks = state.syncedTasks + state.failedTasks;
      const globalProgress = state.totalTasks ? Math.round((processedTasks / state.totalTasks) * 100) : 100;
      const itemProgress = state.currentItemTotal ? Math.round((state.currentItemDone / state.currentItemTotal) * 100) : 0;
      const photoProgress = state.currentPhotoPercent;
      const isSavingStage = state.currentTaskStage === 'saving';
      const isUploadingStage = state.currentTaskStage === 'uploading';
      const pausedStatusText = state.status === 'running' ? this.getPausedStatusText(state.logs) : '';
      const isPausedDuringSaving = isSavingStage && !!pausedStatusText;
      return {
        ...state,
        globalProgress,
        processedTasks,
        taskProgressLabel: this.getTaskProgressLabel(state.currentTaskStage),
        itemProgress,
        photoProgress: isPausedDuringSaving ? 0 : photoProgress,
        photoProgressMode: isSavingStage && !isPausedDuringSaving ? 'indeterminate' : 'determinate',
        showPhotoProgress: isSavingStage || isUploadingStage,
        photoProgressLabel: isSavingStage
          ? this.translate.instant('SYNC_PROGRESS.SAVING_IN_PROGRESS_LABEL')
          : this.translate.instant('SYNC_PROGRESS.PHOTO_LABEL', { current: state.currentPhotoIndex, total: state.currentItemTotal }),
        resultBannerClass: state.status === 'success' ? 'success' : (state.status === 'interrupted' ? 'error' : 'neutral'),
        failureReasonText: state.failureReasonKey
          ? this.translate.instant(state.failureReasonKey, state.failureReasonParams ?? {})
          : '',
        canClose: state.status !== 'running',
        canStop: state.status === 'running',
        statusText: this.getStatusText(state),
        pausedStatusText,
        sortedTaskResults: [...state.taskResults].sort((a, b) => a.taskId - b.taskId)
      };
    })
  );

  constructor(
    private dialogRef: MatDialogRef<SyncProgressDialogComponent>,
    private syncProgressService: SyncProgressService,
    private tasksService: TasksService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private sessionService: SessionService,
    private router: Router
  ) {
    this.dialogRef.disableClose = true;
    this.sessionService.setSyncDialogOpen(true);
    this.activateHistoryTrap();
  }

  ngAfterViewInit(): void {
    this.sub.add(
      this.state$.pipe(
        map(state => state.logs.length),
        distinctUntilChanged()
      ).subscribe(() => this.scrollLogsToBottom())
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sessionService.setSyncDialogOpen(false);
    this.releaseHistoryTrap();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.syncProgressService.snapshot.status === 'running') {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  close(): void {
    if (this.syncProgressService.snapshot.status === 'running') {
      return;
    }
    this.releaseHistoryTrap();
    this.tasksService.refreshTasks();
    this.dialogRef.close();
  }

  confirmStop(): void {
    if (this.syncProgressService.snapshot.status !== 'running') {
      return;
    }
    this.dialogService.showInfoDialog({
      header: 'SYNC_PROGRESS.STOP_CONFIRM_HEADER',
      content: 'SYNC_PROGRESS.STOP_CONFIRM_CONTENT',
      confirmBtn: 'SYNC_PROGRESS.STOP_CONFIRM_OK',
      cancelBtn: 'SYNC_PROGRESS.STOP_CONFIRM_CANCEL'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.tasksService.requestStopSync();
      }
    });
  }

  renderLog(line: string): string {
    const splitIndex = line.indexOf('|');
    if (splitIndex < 0) {
      return this.translate.instant(line);
    }

    const key = line.substring(0, splitIndex);
    const rawParams = line.substring(splitIndex + 1);
    try {
      const params = JSON.parse(rawParams) as Record<string, any>;
      const { __ts, ...translateParams } = params;
      const message = this.translate.instant(key, translateParams);
      const tsPrefix = this.formatLogTimestamp(__ts);
      return tsPrefix ? `${tsPrefix} ${message}` : message;
    } catch {
      return this.translate.instant(key);
    }
  }

  getLogsText(lines: string[]): string {
    return lines.map(line => this.renderLog(line)).join('\n');
  }

  renderTaskResultReason(result: SyncTaskResult): string {
    if (result.status === 'success') {
      return this.translate.instant('SYNC_PROGRESS.RESULT_SUCCESS');
    }

    if (!result.reasonKey) {
      return this.translate.instant('SYNC_PROGRESS.FAILURE_REASON_UNKNOWN');
    }

    const params = { ...(result.reasonParams ?? {}) } as Record<string, any>;
    if (typeof params.message === 'string' && params.message.includes('.')) {
      params.message = this.translate.instant(params.message, params);
    }
    if (typeof params.taskStatus === 'string') {
      params.taskStatus = this.translate.instant(`TASKS.STATUS.${params.taskStatus}`);
    }

    if (result.reasonKey === 'SYNC_PROGRESS.FAILURE_REASON_CLIENT' && typeof params.code === 'string') {
      const backendCodeKey = `ERRORS.${params.code}`;
      const translatedBackendError = this.translate.instant(backendCodeKey, params);
      if (translatedBackendError !== backendCodeKey) {
        return this.translate.instant('SYNC_PROGRESS.FAILURE_REASON_FROM_ERROR_CODE', {
          reason: translatedBackendError
        });
      }
    }

    const translated = this.translate.instant(result.reasonKey, params);
    if (translated !== result.reasonKey) {
      return translated;
    }

    if (result.reasonKey === 'SYNC_PROGRESS.FAILURE_REASON_TASK_STATUS') {
      const statusText = typeof params.taskStatus === 'string' ? params.taskStatus : '';
      return this.translate.instant('SYNC_PROGRESS.FAILURE_REASON_TASK_STATUS_FALLBACK', {
        taskStatus: statusText
      });
    }

    return this.translate.instant('SYNC_PROGRESS.FAILURE_REASON_UNKNOWN');
  }

  getTaskResultOriginalMessage(result: SyncTaskResult): string {
    if (result.status !== 'failed') {
      return '';
    }
    const rawMessage = result.reasonParams?.['message'];
    if (typeof rawMessage !== 'string' || !rawMessage.trim()) {
      return '';
    }

    if (rawMessage.startsWith('SYNC_PROGRESS.') || rawMessage.startsWith('ERRORS.')) {
      return '';
    }

    return rawMessage;
  }

  getTaskResultHeader(result: SyncTaskResult): string {
    const key = result.status === 'success'
      ? 'SYNC_PROGRESS.RESULT_TASK_SUCCESS'
      : 'SYNC_PROGRESS.RESULT_TASK_FAILED';
    return this.translate.instant(key, { taskId: result.taskId });
  }

  getPhotoIssueReason(issue: SyncPhotoIssue): string {
    return this.translate.instant(`SYNC_PROGRESS.PHOTO_ISSUE_REASON_${issue.reason}`);
  }

  getPhotoIssuePath(issue: SyncPhotoIssue): string {
    return issue.entryPath.join(' / ');
  }

  goToTask(result: SyncTaskResult, issue?: SyncPhotoIssue): void {
    if (this.syncProgressService.snapshot.status === 'running') {
      return;
    }
    this.releaseHistoryTrap();
    this.dialogRef.close();
    void this.router.navigate([GO_URL, TASKS_URL, result.taskId], {
      queryParams: issue ? {
        syncIssueEntryId: issue.entryId,
        syncIssueCloneIndex: issue.cloneIndex
      } : undefined
    });
  }

  canDeleteOfflineData(result: SyncTaskResult): boolean {
    return result.status === 'failed'
      && (
        result.reasonKey === 'SYNC_PROGRESS.FAILURE_REASON_CLIENT'
        || result.reasonKey === 'SYNC_PROGRESS.FAILURE_REASON_TASK_STATUS'
      )
      && !this.deletedOfflineTaskIds.has(result.taskId);
  }

  isOfflineDataDeleted(taskId: number): boolean {
    return this.deletedOfflineTaskIds.has(taskId);
  }

  deleteOfflineData(taskId: number): void {
    if (this.deletingTaskIds.has(taskId)) {
      return;
    }
    this.deletingTaskIds.add(taskId);
    this.tasksService.deleteOfflineTaskData(taskId).subscribe({
      complete: () => {
        this.deletedOfflineTaskIds.add(taskId);
        this.deletingTaskIds.delete(taskId);
      },
      error: () => {
        this.deletingTaskIds.delete(taskId);
      }
    });
  }

  private getPausedStatusText(lines: string[]): string {
    if (!lines.length) {
      return '';
    }
    const pauseKeys = new Set([
      'SYNC_PROGRESS.LOG_PAUSED_OFFLINE',
      'SYNC_PROGRESS.LOG_PAUSED_TIMEOUT',
      'SYNC_PROGRESS.LOG_PAUSED_SERVER'
    ]);
    const resumeActivityKeys = new Set([
      'SYNC_PROGRESS.LOG_UPLOADING_ITEM_START',
      'SYNC_PROGRESS.LOG_UPLOADING_ITEM',
      'SYNC_PROGRESS.LOG_SAVING_TASK',
      'SYNC_PROGRESS.LOG_CLEANING',
      'SYNC_PROGRESS.LOG_TASK_SUCCESS',
      'SYNC_PROGRESS.LOG_TASK_FAILED',
      'SYNC_PROGRESS.LOG_TASK_START',
      'SYNC_PROGRESS.LOG_MISSING_DATA',
      'SYNC_PROGRESS.LOG_NO_ATTACHMENTS'
    ]);

    let latestPauseIndex = -1;
    let latestPauseLine = '';
    let latestResumeActivityIndex = -1;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const splitIndex = line.indexOf('|');
      const key = splitIndex < 0 ? line : line.substring(0, splitIndex);
      if (pauseKeys.has(key)) {
        latestPauseIndex = i;
        latestPauseLine = line;
      }
      if (resumeActivityKeys.has(key)) {
        latestResumeActivityIndex = i;
      }
    }

    if (latestPauseIndex === -1) {
      return '';
    }

    return latestPauseIndex > latestResumeActivityIndex ? this.renderLog(latestPauseLine) : '';
  }

  private scrollLogsToBottom(): void {
    setTimeout(() => {
      const textarea = this.logTextarea?.nativeElement;
      if (!textarea) {
        return;
      }
      textarea.scrollTop = textarea.scrollHeight;
    });
  }

  private formatLogTimestamp(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const datePart = date.toLocaleDateString();
    const timePart = date.toLocaleTimeString();
    return `[${datePart} ${timePart}]`;
  }

  private activateHistoryTrap(): void {
    if (this.historyTrapActive) {
      return;
    }

    window.history.pushState({ syncDialog: true }, '', window.location.href);
    window.addEventListener('popstate', this.handlePopState);
    this.historyTrapActive = true;
  }

  private releaseHistoryTrap(): void {
    if (!this.historyTrapActive) {
      return;
    }

    window.removeEventListener('popstate', this.handlePopState);
    this.historyTrapActive = false;
  }

  private readonly handlePopState = (): void => {
    if (!this.historyTrapActive) {
      return;
    }

    setTimeout(() => window.history.go(1), 0);
  }

  private getTaskProgressLabel(stage: SyncProgressState['currentTaskStage']): string {
    if (stage === 'uploading') {
      return this.translate.instant('SYNC_PROGRESS.TASK_STAGE_UPLOADING');
    }
    if (stage === 'saving') {
      return this.translate.instant('SYNC_PROGRESS.TASK_STAGE_SAVING');
    }
    if (stage === 'cleaning') {
      return this.translate.instant('SYNC_PROGRESS.TASK_STAGE_CLEANING');
    }
    return this.translate.instant('SYNC_PROGRESS.TASK_STAGE_IDLE');
  }

  private getStatusText(state: SyncProgressState): string {
    if (state.status === 'success') {
      return this.translate.instant('SYNC_PROGRESS.SUCCESS', {
        synced: state.syncedTasks,
        failed: state.failedTasks,
        total: state.totalTasks
      });
    }
    if (state.status === 'interrupted') {
      return this.translate.instant('SYNC_PROGRESS.INTERRUPTED', {
        synced: state.syncedTasks,
        failed: state.failedTasks,
        total: state.totalTasks
      });
    }
    if (state.status === 'stopped') {
      return this.translate.instant('SYNC_PROGRESS.STOPPED', {
        synced: state.syncedTasks,
        failed: state.failedTasks,
        total: state.totalTasks
      });
    }
    return this.translate.instant('SYNC_PROGRESS.RUNNING');
  }
}
